<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <!-- Title Bar -->
      <div class="title-bar">
        <h2 class="modal-title">Cargar nuevo colaborador</h2>
        <button class="close-button" @click="$emit('close')">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 12L12 20M12 12L20 20"
              stroke="#C5C5C5"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <div class="form-content">
        <CustomInput
          id="names"
          v-model="formData.names"
          label="Nombre(s)"
          placeholder="Escribe el nombre del colaborador"
          :full-width="true"
          :uppercase="true"
        />

        <CustomInput
          id="paternal-lastname"
          v-model="formData.paternalLastName"
          label="Apellido paterno"
          placeholder="Escribe el apellido paterno del colaborador"
          :full-width="true"
          :uppercase="true"
        />

        <CustomInput
          id="maternal-lastname"
          v-model="formData.maternalLastName"
          label="Apellido materno"
          placeholder="Escribe el apellido materno del colaborador"
          :full-width="true"
          :uppercase="true"
        />

        <CustomInput
          id="rfc"
          v-model="formData.rfc"
          label="RFC del colaborador"
          placeholder="Escribe el RFC a 10 o 13 caracteres"
          :maxlength="13"
          :uppercase="true"
          :show-error="!isRfcValid && formData.rfc.trim() !== ''"
          :error-message="getRfcErrorMessage()"
          error-type="error"
          @input="validateRfc"
        />

        <div class="two-column-row">
          <CustomInput
            id="email"
            v-model="formData.email"
            label="Correo electrónico del colaborador"
            placeholder="Escribe el correo electrónico"
            :uppercase="false"
            :show-error="!isEmailValid && formData.email.trim() !== ''"
            :error-message="getEmailErrorMessage()"
            error-type="error"
            @input="validateEmail"
          />
          <CustomInput
            id="phone"
            v-model="formData.phone"
            label="Teléfono móvil del colaborador"
            placeholder="Escribe el número a 10 dígitos"
            type="text"
            :maxlength="10"
            :numeric-only="true"
            :show-error="!isPhoneValid && formData.phone.trim() !== ''"
            :error-message="getPhoneErrorMessage()"
            error-type="error"
            @input="validatePhone"
          />
        </div>

        <div class="two-column-row">
          <div class="input-field">
            <label class="input-title">Periodicidad de pago</label>
            <div class="radio-options">
              <label
                v-for="payPeriod in paymentFrequencyOptions"
                :key="payPeriod"
                :class="['radio-option', { selected: formData.paymentFrequency === payPeriod }]"
              >
                <input
                  type="radio"
                  :value="payPeriod"
                  v-model="formData.paymentFrequency"
                  class="radio-input"
                  required
                />
                <span class="custom-radio"></span>
                {{ payPeriod }}
              </label>
            </div>
          </div>
          <div class="input-field">
            <label class="input-title">Cobertura del Seguro Inteligente Mutuus</label>
            <div class="input-box">
              <CustomSelect
                id="membership-type"
                v-model="formData.membershipType"
                :options="membershipOptions"
                option-value="value"
                option-label="label"
                placeholder="Selecciona una opción"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="buttons-container">
        <CustomButton
          text="Cancelar"
          variant="cancel"
          @click="$emit('close')"
          :disabled="isLoading"
        />
        <CustomButton
          text="Cargar colaborador"
          variant="secondary"
          :show-icon="true"
          :icon-src="addUserIcon"
          icon-alt="Cargar colaborador"
          :disabled="!isFormValid || isLoading"
          @click="handleSubmit"
        />
      </div>
    </div>

    <CustomLoading :is-visible="isLoading" text="Procesando..." />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue'
import CustomButton from './CustomButton.vue'
import CustomSelect from './CustomSelect.vue'
import CustomInput from './CustomInput.vue'
import CustomLoading from './CustomLoading.vue'
import addUserIcon from '@/assets/add-user.svg'
import { ProductName } from 'commons'
import { DataValidatorService } from 'commons'

interface Props {
  isVisible: boolean
  isLoading?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  submit: [data: unknown]
}>()

const dataValidatorService = inject<DataValidatorService>('dataValidatorService')

if (!dataValidatorService) {
  throw new Error('dataValidatorService is not provided')
}

const formData = ref({
  names: '',
  paternalLastName: '',
  maternalLastName: '',
  email: '',
  phone: '',
  rfc: '',
  membershipType: '',
  paymentFrequency: '',
})

const membershipOptions = ref([
  { value: ProductName.MM1, label: ProductName.MM1 },
  { value: ProductName.MM2, label: ProductName.MM2 },
])

const paymentFrequencyOptions = ref<string[]>(['Mensual', 'Anual'])

const isEmailValid = ref(true)
const isPhoneValid = ref(true)
const isRfcValid = ref(true)

watch(
  () => props.isVisible,
  (newValue) => {
    if (!newValue) {
      resetForm()
    }
  },
)

const resetForm = () => {
  formData.value = {
    names: '',
    paternalLastName: '',
    maternalLastName: '',
    email: '',
    phone: '',
    rfc: '',
    membershipType: '',
    paymentFrequency: '',
  }
  isEmailValid.value = true
  isPhoneValid.value = true
  isRfcValid.value = true
}

const validateEmail = () => {
  const email = formData.value.email.trim()
  if (email === '') {
    isEmailValid.value = true
    return
  }

  isEmailValid.value = dataValidatorService.isValidEmail(email)
}

const getEmailErrorMessage = () => {
  return 'Correo electrónico inválido'
}

const validatePhone = () => {
  const phone = formData.value.phone.trim()
  if (phone === '') {
    isPhoneValid.value = true
    return
  }

  isPhoneValid.value = dataValidatorService.isValidPhone(phone)
}

const getPhoneErrorMessage = () => {
  return 'Teléfono debe tener exactamente 10 dígitos'
}

const validateRfc = () => {
  const rfc = formData.value.rfc.trim()
  if (rfc === '') {
    isRfcValid.value = true
    return
  }

  isRfcValid.value = dataValidatorService.isValidRfc(rfc)
}

const getRfcErrorMessage = () => {
  return 'RFC inválido (debe tener 10 o 13 caracteres)'
}

const isFormValid = computed(() => {
  return (
    formData.value.names.trim() !== '' &&
    formData.value.paternalLastName.trim() !== '' &&
    formData.value.maternalLastName.trim() !== '' &&
    formData.value.email.trim() !== '' &&
    formData.value.phone.trim() !== '' &&
    formData.value.rfc.trim() !== '' &&
    formData.value.membershipType !== '' &&
    formData.value.paymentFrequency !== '' &&
    isEmailValid.value &&
    isPhoneValid.value &&
    isRfcValid.value
  )
})

const handleOverlayClick = () => {
  emit('close')
}

const handleSubmit = () => {
  if (isFormValid.value) {
    emit('submit', formData.value)
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  width: 1208px;
  max-width: 90vw;
  max-height: 90vh;
  height: 694px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.title-bar {
  width: 100%;
  height: 59px;
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
}

.modal-title {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 130%;
  color: #4f4f4f;
  margin: 0;
  flex: 1;
  text-align: left;
}

.close-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50px;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px 40px;
  gap: 24px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.two-column-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 40px;
  width: 100%;
  height: 70px;
  flex: none;
  order: 3;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  min-width: 0;
}

.two-column-row > * {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
}

.two-column-row :deep(.input-field) {
  width: 100% !important;
  max-width: 100%;
}

@media (max-width: 768px) {
  .two-column-row {
    flex-direction: column;
    height: auto;
    gap: 24px;
  }

  .two-column-row > * {
    width: 100%;
  }
}

.input-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 544px;
  min-width: 0;
  height: 70px;
  flex: 1;
  order: 0;
  box-sizing: border-box;
  max-width: 100%;
}

@media (max-width: 768px) {
  .input-field {
    width: 100%;
    height: auto;
  }
}

.input-title {
  width: auto;
  height: 22px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  margin: 0;
}

.input-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  width: 100%;
  height: 48px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  position: relative;
}

.buttons-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0 40px 32px 40px;
  gap: 40px;
  width: 100%;
  height: 60px;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
  flex-shrink: 0;
}

.cancel-button {
  width: 72px;
  height: 22px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #1a1a1a;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.radio-options {
  display: flex;
  gap: 16px;
  width: 100%;
}

.radio-option {
  display: flex;
  align-items: center !important;
  border: 2px solid #d3d3d3;
  border-radius: 12px;
  padding: 8px 24px;
  cursor: pointer;
  background: #fff;
  transition:
    border 0.2s,
    background 0.2s;
  font-size: 1.1rem;
  position: relative;
  color: #555;
  width: 100%;
  justify-content: center;
}

.radio-option.selected {
  border-color: #8cd211;
  background: #f7fef5;
}

.radio-input {
  display: none;
}

.radio-input:disabled + .custom-radio {
  opacity: 0.5;
  cursor: not-allowed;
}

.radio-input:disabled ~ span {
  opacity: 0.5;
}

.custom-radio {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 2px solid #8cd211;
  border-radius: 50%;
  margin-right: 8px;
  background: #fff;
  position: relative;
}

.radio-option.selected .custom-radio {
  background: #f7fef5;
}

.radio-input:checked + .custom-radio::after {
  content: '';
  display: block;
  width: 12px;
  height: 12px;
  background: #8cd211;
  border-radius: 50%;
  position: absolute;
  top: 4px;
  left: 4px;
}
</style>
